<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { typeOwner, type Field, type ObjectRecord } from '@vp/shared-types';
import { live, ui } from '../editor/bridge';
import { buildSrcdoc, vSandbox } from '../editor/sandbox';
import { commandsFor, fromSandbox, moduleView, onViewPost, runCommand, sendViewMessage, typeContribution } from '../modules';
import { t, tm } from '../i18n';

/**
 * Module properties from the declarative field schema (§6.4): rendered by the core in
 * a single style and localized; `panel` fields embed the module's sandboxed panel.
 */
const props = defineProps<{ obj: ObjectRecord }>();
const info = computed(() => typeContribution(props.obj.type));
const moduleId = computed(() => typeOwner(props.obj.type) ?? '');
const fields = computed<Field[]>(() => info.value?.contribution.fields ?? []);
const plain = computed(() => fields.value.filter((f) => f.type !== 'panel'));
const panels = computed(() => fields.value.filter((f) => f.type === 'panel'));
const cmds = computed(() => {
  void ui.docRev;
  return commandsFor([props.obj]);
});
const panelHtml = ref<string | null>(null);
const panelFrame = ref<HTMLIFrameElement | null>(null);
let unsub: (() => void) | null = null;

function get(key: string): unknown {
  let cur: unknown = props.obj.props;
  for (const k of key.split('.')) cur = cur && typeof cur === 'object' ? (cur as Record<string, unknown>)[k] : undefined;
  return cur;
}

function set(key: string, value: unknown): void {
  const parts = key.split('.');
  const root = parts[0];
  let v: unknown = value;
  if (parts.length > 1) {
    const base = structuredClone((props.obj.props[root] as Record<string, unknown>) ?? {});
    let cur = base;
    for (let i = 1; i < parts.length - 1; i++) cur = (cur[parts[i]] ??= {}) as Record<string, unknown>;
    cur[parts[parts.length - 1]] = value;
    v = base;
  }
  live.editor?.setProps([props.obj.id], { [root]: v }, 'module.props');
}

function label(f: Field): string {
  return tm(moduleId.value, f.label);
}

async function loadPanel(): Promise<void> {
  panelHtml.value = null;
  if (!panels.value.length || !info.value) return;
  try {
    panelHtml.value = buildSrcdoc(await moduleView(props.obj.type, 'panel', props.obj.id));
  } catch {
    panelHtml.value = null;
  }
}

function onMessage(e: MessageEvent): void {
  const d = e.data as { vpView?: number; data?: unknown } | null;
  if (!d || d.vpView !== 1 || !panelFrame.value || e.source !== panelFrame.value.contentWindow) return;
  if (ui.readOnly || !fromSandbox(moduleId.value, d.data)) return;
  sendViewMessage(moduleId.value, props.obj.id, d.data);
}

onMounted(() => {
  window.addEventListener('message', onMessage);
  unsub = onViewPost((_m, objectId, data) => {
    if (objectId === props.obj.id) panelFrame.value?.contentWindow?.postMessage({ vpView: 1, data }, '*');
  });
  void loadPanel();
});
onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  unsub?.();
});
watch(() => props.obj.id, loadPanel);
</script>

<template>
  <section v-if="info && (plain.length || panels.length || cmds.length)" class="mod">
    <div v-for="f in plain" :key="f.key" class="row">
      <label class="lbl" :for="'mf-' + f.key">{{ label(f) }}</label>
      <input v-if="f.type === 'text' && !f.multiline" :id="'mf-' + f.key" class="num-pill wide" :value="String(get(f.key) ?? '')" :maxlength="f.maxLength" :disabled="ui.readOnly" @change="set(f.key, ($event.target as HTMLInputElement).value)" />
      <textarea v-else-if="f.type === 'text'" :id="'mf-' + f.key" class="num-pill wide" rows="3" :value="String(get(f.key) ?? '')" :disabled="ui.readOnly" @change="set(f.key, ($event.target as HTMLTextAreaElement).value)"></textarea>
      <input
        v-else-if="f.type === 'number'"
        :id="'mf-' + f.key"
        type="number"
        class="num-pill"
        :min="f.min"
        :max="f.max"
        :step="f.step ?? 'any'"
        :value="Number(get(f.key) ?? f.default ?? 0)"
        :disabled="ui.readOnly"
        @change="set(f.key, +($event.target as HTMLInputElement).value)"
      />
      <input v-else-if="f.type === 'color'" :id="'mf-' + f.key" type="color" class="swatch-btn color-in" :value="String(get(f.key) ?? f.default ?? '#000000').slice(0, 7)" :disabled="ui.readOnly" @change="set(f.key, ($event.target as HTMLInputElement).value)" />
      <input v-else-if="f.type === 'date'" :id="'mf-' + f.key" type="date" class="num-pill wide" :value="String(get(f.key) ?? f.default ?? '')" :disabled="ui.readOnly" @change="set(f.key, ($event.target as HTMLInputElement).value)" />
      <input v-else-if="f.type === 'toggle'" :id="'mf-' + f.key" type="checkbox" :checked="!!(get(f.key) ?? f.default)" :disabled="ui.readOnly" @change="set(f.key, ($event.target as HTMLInputElement).checked)" />
      <select v-else-if="f.type === 'select'" :id="'mf-' + f.key" class="num-pill wide" :value="get(f.key) ?? f.default" :disabled="ui.readOnly" @change="set(f.key, ($event.target as HTMLSelectElement).value)">
        <option v-for="o in f.options" :key="String(o.value)" :value="o.value">{{ tm(moduleId, o.label) }}</option>
      </select>
    </div>
    <template v-for="p in panels" :key="p.key">
      <p class="panel-label">{{ label(p) }}</p>
      <iframe v-if="panelHtml" ref="panelFrame" class="panel" :data-module="moduleId" v-sandbox="panelHtml" :style="{ height: (p.type === 'panel' ? p.height ?? 120 : 120) + 'px', pointerEvents: ui.readOnly ? 'none' : 'auto' }" :title="label(p)"></iframe>
    </template>
    <div v-if="cmds.length && !ui.readOnly" class="cmds">
      <button v-for="c in cmds" :key="c.moduleId + c.id" class="pill" @click="runCommand(c.moduleId, c.id)">{{ c.label }}</button>
    </div>
  </section>
  <section v-else-if="!info" class="mod unavailable">{{ t('props.unavailable') }}</section>
</template>

<style scoped>
.mod {
  padding: 4px 0;
  border-top: 1px solid var(--border);
}
.wide {
  width: 140px;
  max-width: 60%;
  min-width: 0;
}
textarea.wide {
  border-radius: 12px;
  height: auto;
  padding: 6px 10px;
}
.panel-label {
  margin: 8px 0 4px;
  color: var(--text-2);
  font-size: 12px;
}
.panel {
  width: 100%;
  border: none;
  display: block;
}
.cmds {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.unavailable {
  color: var(--warn);
  font-size: 12px;
}
</style>
<style>
input[type='color'].color-in {
  -webkit-appearance: none;
  appearance: none;
  overflow: hidden;
}
input[type='color'].color-in::-webkit-color-swatch-wrapper {
  padding: 0;
}
input[type='color'].color-in::-webkit-color-swatch {
  border: none;
  border-radius: 999px;
}
input[type='color'].color-in::-moz-color-swatch {
  border: none;
  border-radius: 999px;
}
</style>
